import { SyncService } from "frontend/services/syncService";
import { useSortedFrames } from "frontend/subscriptions";
import EditableText, { EditableTextStyle } from "frontend/ui-components/editable-text";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import style from "./frames-list.module.css";
import { DragDropContext, Droppable, Draggable, DraggableProvided } from "react-beautiful-dnd";
import { ElementDrawProps } from "shared/datamodel/canvas-element";
import { useCallback, useRef, useState } from "react";
import Modal from "frontend/modal/modal";
import { createMenuOption, OptionsContextMenu } from "frontend/context-menu/optionsContextMenu";
import { useHover } from "usehooks-ts";
import { Frame } from "shared/datamodel/schemas";
import { ClosedEyeIcon } from "frontend/icons/closed-eye-icon";
import { OpenedEyeIcon } from "frontend/icons/opened-eye-icon";
import { TrashIcon } from "frontend/icons/trash-icon";
import StyledButton, { ButtonSize, ButtonStyle } from "frontend/ui-components/styled-button";
import { CursorType } from "./cursor-type";
import { getPathPrefix } from "../utils/getPathPrefix";
import classNames from "classnames";
import { useIsCIMReorderEnabled } from "utils/url-utils";

export function FramesList({
  syncService,
  onFrameSelected,
  onFrameDeleted,
  editEnabled = true,
  menuEnabled = true,
  showHidden = true,
  onCursorTypeChange,
}: {
  syncService: SyncService<RW> | null;
  onFrameSelected: (id: string) => void;
  onFrameDeleted: (id: string) => void;
  editEnabled?: boolean;
  menuEnabled?: boolean;
  showHidden?: boolean;
  onCursorTypeChange?: (type: CursorType) => void;
}) {
  const isCIMReorderEnabled = useIsCIMReorderEnabled();
  let { sortedFrames } = useSortedFrames(syncService?.getReplicache());
  if (!showHidden) {
    sortedFrames = sortedFrames.filter((f) => typeof f.value.visible === "undefined" || f.value.visible);
  }

  function onDragEnd(result: any) {
    const { source, destination } = result;
    if (!source || !destination) {
      return;
    }
    const fromIndex = source.index;
    const toIndex = destination.index;
    const frame = sortedFrames[fromIndex];
    sortedFrames.splice(fromIndex, 1);
    sortedFrames.splice(toIndex, 0, frame);

    const elements: [string, ElementDrawProps][] = sortedFrames.map((frame, index) => [
      `${frame.type}-${frame.id}`,
      { sortIndex: index },
    ]);
    syncService?.mutate.changeElements({ info: elements });
  }

  function renderFramesEmptyState() {
    return (
      <div className={style.framesEmptyState}>
        <img src={getPathPrefix("/images/no-frames.png")} />
        <div>
          <p>No frames yet! </p>
          <p>Add a frame to this Canvas.</p>
        </div>
        <StyledButton
          title={"Create frame"}
          style={ButtonStyle.outline}
          size={ButtonSize.small}
          onClick={() => {
            onCursorTypeChange && onCursorTypeChange(CursorType.frame);
          }}
          tabIndex={null}
        />
      </div>
    );
  }

  function renderFrames() {
    return !sortedFrames.length
      ? renderFramesEmptyState()
      : sortedFrames.map((frame, index) => (
          <Draggable key={frame.id} draggableId={frame.id} index={index} isDragDisabled={!editEnabled}>
            {(provided) => (
              <FrameCell
                index={index + 1}
                frame={frame}
                syncService={syncService}
                onClick={() => onFrameSelected(`${frame.type}-${frame.id}`)}
                onDelete={() => onFrameDeleted(`${frame.type}-${frame.id}`)}
                editEnabled={editEnabled}
                provided={provided}
                menuEnabled={menuEnabled}
              />
            )}
          </Draggable>
        ));
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable" direction="vertical" isDropDisabled={!editEnabled}>
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={classNames(style.list, isCIMReorderEnabled && style.cimList)}
          >
            {renderFrames()}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function FrameCell({
  index,
  frame,
  syncService,
  onClick,
  onDelete,
  editEnabled,
  provided,
  menuEnabled = true,
}: {
  index: number;
  frame: any;
  syncService: SyncService<RW> | null;
  onClick: () => void;
  onDelete: () => void;
  editEnabled: boolean;
  provided: DraggableProvided;
  menuEnabled?: boolean;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [focused, setFocused] = useState(false);
  const menuButtonRef = useRef<HTMLDivElement>(null);

  const ref = useRef<HTMLDivElement | null>(null);
  const hover = useHover(ref);

  const setRef = useCallback(
    (r) => {
      provided.innerRef(r);
      ref.current = r;
    },
    [provided.innerRef]
  );

  function menuClicked(e: any) {
    e.stopPropagation();
    setShowMenu((show) => !show);
  }

  function backgroundClicked() {
    setShowMenu(false);
  }

  function renameClicked(e: any) {
    e.stopPropagation();
    setShowMenu(false);
    setFocused(true);
  }

  function setVisible(e: any, visible: boolean) {
    e.stopPropagation();
    setShowMenu(false);
    const id = `${frame.type}-${frame.id}`;
    syncService?.mutate.changeElementProperties({
      id,
      properties: [{ key: "visible", value: visible }],
    });
  }

  function deleteClicked(e: any) {
    e.stopPropagation();
    setShowMenu(false);
    onDelete();
  }

  function options(frame: Frame) {
    const { visible = true } = frame;
    const options = [
      createMenuOption({
        title: "Rename",
        iconPath: getPathPrefix("/images/rename-frame-icon.svg"),
        id: "rename",
        onClick: renameClicked,
      }),
    ];
    if (visible) {
      options.push({
        title: "Hide",
        icon: <ClosedEyeIcon color={"white"} size={17} />,
        id: "hide",
        onClick: (e) => setVisible(e, false),
      });
    } else {
      options.push({
        title: "Show",
        icon: <OpenedEyeIcon color={"white"} size={17} />,
        id: "show",
        onClick: (e) => setVisible(e, true),
      });
    }
    options.push({
      title: "Delete",
      icon: <TrashIcon color={"white"} size={17} />,
      id: "delete",
      onClick: deleteClicked,
    });
    return options;
  }

  const { visible = true } = frame.value;

  const renderVisibleText = () => (
    <EditableText
      value={frame.value.title}
      editable={editEnabled && visible}
      textStyle={EditableTextStyle.regular}
      focus={focused}
      onEditing={(editing: boolean) => setFocused(editing)}
      onChange={(title: string) => {
        const id = `${frame.type}-${frame.id}`;
        syncService?.mutate.changeElementProperties({
          id,
          properties: [{ key: "title", value: title }],
        });
      }}
    />
  );

  const renderHiddenFrameText = () => (
    <div className={style.hiddenFrameText}>
      <span>{frame.value.title}</span>
      <ClosedEyeIcon size={20} color={"#797E93"} />
    </div>
  );

  return (
    <div
      ref={setRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      className={style.cell}
      onClick={onClick}
    >
      <div className={style.index}>{index}.</div>
      {visible ? renderVisibleText() : renderHiddenFrameText()}
      {menuEnabled && (hover || showMenu) && (
        <div className={style.menuIcon} onClick={menuClicked} ref={menuButtonRef} />
      )}
      {showMenu && (
        <Modal dimBackground={false}>
          <OptionsContextMenu
            options={options(frame.value)}
            attachToRef={menuButtonRef}
            onDismiss={backgroundClicked}
          />
        </Modal>
      )}
    </div>
  );
}
